<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { updater } from '../services/updateManager'

const showNotes = ref(false)

watch(() => updater.availableVersion.value, () => {
  showNotes.value = false
})

const title = computed(() => {
  if (updater.status.value === 'downloading') return `Downloading ${updater.availableVersion.value ?? 'update'}…`
  if (updater.status.value === 'ready') return `Version ${updater.availableVersion.value} is ready`
  if (updater.status.value === 'installing') return 'Installing update…'
  return `Version ${updater.availableVersion.value} is available`
})

const progressLabel = computed(() => {
  const progress = updater.downloadProgress.value
  return progress === null ? 'Downloading in the background' : `${progress}% downloaded`
})
</script>

<template>
  <Teleport to="body">
    <Transition name="update-notice">
      <aside v-if="updater.noticeVisible.value" class="update-notice" role="status" aria-live="polite">
        <button
          v-if="updater.status.value !== 'installing'"
          type="button"
          class="update-dismiss"
          aria-label="Dismiss update notice"
          @click="updater.dismissUpdateNotice"
        >×</button>

        <div class="update-kicker">Superelectric Ink update</div>
        <div class="update-title">{{ title }}</div>

        <template v-if="updater.status.value === 'available'">
          <p>An update is available when you are ready.</p>
          <div class="update-actions">
            <button v-if="updater.releaseNotes.value" type="button" class="update-btn" @click="showNotes = !showNotes">
              {{ showNotes ? 'Hide what’s new' : 'What’s new' }}
            </button>
            <button type="button" class="update-btn primary" @click="updater.downloadAvailableUpdate">Download</button>
          </div>
        </template>

        <template v-else-if="updater.status.value === 'downloading'">
          <p>{{ progressLabel }}</p>
          <div v-if="updater.downloadProgress.value !== null" class="update-progress" aria-hidden="true">
            <span :style="{ width: `${updater.downloadProgress.value}%` }" />
          </div>
        </template>

        <template v-else-if="updater.status.value === 'ready'">
          <p>{{ updater.installWhenClosed.value ? 'It will install when you close the app.' : 'Your work will be saved before installation.' }}</p>
          <div class="update-actions">
            <button v-if="updater.releaseNotes.value" type="button" class="update-btn" @click="showNotes = !showNotes">
              {{ showNotes ? 'Hide what’s new' : 'What’s new' }}
            </button>
            <button type="button" class="update-btn" @click="updater.toggleInstallWhenClosed">
              {{ updater.installWhenClosed.value ? 'Don’t install on close' : 'Install when I close' }}
            </button>
            <button type="button" class="update-btn primary" @click="updater.restartAndInstall">Restart now</button>
          </div>
        </template>

        <p v-else>Saving your work and applying the update.</p>
        <p v-if="showNotes && updater.releaseNotes.value" class="update-notes">{{ updater.releaseNotes.value }}</p>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.update-notice {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 100001;
  box-sizing: border-box;
  width: min(430px, calc(100vw - 32px));
  padding: 17px 18px 16px;
  border: 1px solid var(--surface-border, rgba(255, 255, 255, 0.18));
  border-left: 4px solid var(--accent, #ff6b45);
  border-radius: 10px;
  background: var(--surface-bg, #173f3b);
  color: var(--text-normal, #f3ecdf);
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.34);
  font-family: var(--ui-text);
}

.update-kicker {
  margin-bottom: 5px;
  color: var(--text-muted, #b9b3aa);
  font: 600 10px/1.2 var(--ui-mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.update-title {
  padding-right: 22px;
  font-size: 16px;
  font-weight: 650;
}

.update-notice p {
  margin: 8px 0 0;
  color: var(--text-muted, #d0c9be);
  font-size: 12.5px;
  line-height: 1.5;
}

.update-dismiss {
  position: absolute;
  top: 9px;
  right: 10px;
  border: 0;
  background: transparent;
  color: var(--text-muted, #b9b3aa);
  font-size: 20px;
  cursor: pointer;
}

.update-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 7px;
  margin-top: 14px;
}

.update-btn {
  min-height: 31px;
  padding: 0 11px;
  border: 1px solid var(--surface-border, rgba(255, 255, 255, 0.2));
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: 600 11.5px var(--ui-text);
  cursor: pointer;
}

.update-btn.primary {
  border-color: var(--accent, #ff6b45);
  background: var(--accent, #ff6b45);
  color: var(--accent-contrast, #fff);
}

.update-progress {
  height: 4px;
  margin-top: 11px;
  overflow: hidden;
  border-radius: 99px;
  background: color-mix(in srgb, var(--text-normal, #fff) 13%, transparent);
}

.update-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--accent, #ff6b45);
  transition: width 0.15s ease;
}

.update-notes {
  max-height: 150px;
  overflow: auto;
  padding-top: 10px;
  border-top: 1px solid var(--surface-border, rgba(255, 255, 255, 0.16));
  white-space: pre-wrap;
}

.update-notice-enter-active,
.update-notice-leave-active { transition: opacity 0.16s ease, transform 0.16s ease; }
.update-notice-enter-from,
.update-notice-leave-to { opacity: 0; transform: translateY(10px); }

@media (max-width: 560px) {
  .update-notice { right: 16px; bottom: 16px; left: 16px; width: auto; }
}
</style>
